import React, { useState, useEffect, useContext, useMemo } from "react";
//import { renderCount } from '../../../../appHelpers/appLoggers';
import styled from "styled-components";
import { IconButton, List, FlexboxGrid, Tag, Message, toaster } from "rsuite";
import { Loader } from "../../../ui";
import GearIcon from "@rsuite/icons/Gear";
import CopyIcon from "@rsuite/icons/Copy";
import { DateTime } from "luxon";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../appHelpers/axiosInstance";
import { timeAgo } from "../_helpers/TimeAgo";
import { useHistory, useLocation } from "react-router-dom";
import AdminIcon from "@rsuite/icons/Admin";
import PeoplesIcon from "@rsuite/icons/Peoples";
import { InviteUser } from "./Users/Modal/InviteUser";
import { MachinesContext } from "../home/dashboard/_context/MachinesContext";
import { InputContext } from "../_context/InputContext";
import { Avatar } from "../../../../appHelpers/appCommonSC";
import { SectionRibbon, Button } from "../../../ui";
import { CustomerFilter } from "./helpers/CustomerFilter";

export const UsersListFC = (props) => {
  // renderCount(`AdminFC`);
  const history = useHistory();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [displayCustomerUsers, setDisplayCustomerUsers] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [unaffiliatedUsers, setUnaffiliatedUsers] = useState([]);
  const [displayUnaffiliatedUsers, setDisplayUnaffiliatedUsers] = useState([]);
  const [basicUsers, setBasicUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [superUsers, setSuperUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userSiteGroups, setUserSiteGroups] = useState({});
  const [cardsVisible, setCardsVisible] = useState(false);
  const [filters, setFilters] = useState({
    searchQuery: "",
    customer: null,
    hideDeactivated: "all",
    role: null,
  });
  const { getAccessTokenSilently } = useAuth0();

  const { adminCustomerToggle, setAdminCustomerToggle } =
    useContext(InputContext);

  const { loadingMachines, machines } = useContext(MachinesContext);

  const getUserList = async () => {
    setLoadingUsers(true);
    setCardsVisible(false);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const headers = {
        authorization: `Bearer ${token}`,
      };

      const userRes = await axiosInstance({
        url: `/admin/user/list`,
        data: ``,
        headers: headers,
      });

      // console.log("DEBUG - Full API Response:", userRes?.data);
      // console.log("DEBUG - User Site Groups Data:", userRes?.data[2]);
      setUsers(userRes?.data[0]);
      setCustomerUsers(userRes?.data[1]);
      setUserSiteGroups(userRes?.data[2] || {});
      setUnaffiliatedUsers(
        userRes?.data[0].filter(
          (user) =>
            !userRes?.data[1]
              .flatMap((customer) => customer.clients)
              .includes(user.email_address),
        ),
      );
      setBasicUsers(
        userRes?.data[0].filter((user) =>
          user.roles["roles"].includes("basic"),
        ),
      );
      setAdminUsers(
        userRes?.data[0].filter((user) =>
          user.roles["roles"].includes("admin"),
        ),
      );
      setSuperUsers(
        userRes?.data[0].filter((user) =>
          user.roles["roles"].includes("superusers"),
        ),
      );
    } catch (error) {
      console.error(error);
    }

    setLoadingUsers(false);
    // Trigger staggered animation immediately for better perceived performance
    setCardsVisible(true);
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";

    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, "");

    // Check if it's a US phone number (10 digits)
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }

    // Check if it's a US phone number with country code (11 digits starting with 1)
    if (cleaned.length === 11 && cleaned[0] === "1") {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }

    // Return original if not a standard US format
    return phoneNumber;
  };

  const copyToClipboard = (text, successMessage) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toaster.push(
          <Message showIcon type="success" closable>
            {successMessage}
          </Message>,
          { placement: "topEnd", duration: 2000 },
        );
      },
      (err) => {
        toaster.push(
          <Message showIcon type="error" closable>
            Failed to copy
          </Message>,
          { placement: "topEnd", duration: 2000 },
        );
      },
    );
  };

  const copyAllCustomerEmails = (customer) => {
    const emails = customer.clients
      .map((clientEmail) => {
        const user = users.find((u) => u.email_address === clientEmail);
        return user ? user.email_address : null;
      })
      .filter(Boolean)
      .join(", ");

    copyToClipboard(emails, `Copied ${customer.clients.length} email(s)`);
  };

  const getUserSiteGroupsDisplay = (userEmail) => {
    // Try both lowercase and original case to handle potential case mismatches from backend
    const groups =
      userSiteGroups[userEmail] ||
      userSiteGroups[userEmail?.toLowerCase()] ||
      [];

    // Debug logging to help identify the issue
    /*if (groups.length === 0) {
      console.log(`No site groups found for ${userEmail}`);
      console.log(
        "Available userSiteGroups keys:",
        Object.keys(userSiteGroups),
      );
    }*/

    if (groups.length === 0) {
      return <NoSiteGroups>None</NoSiteGroups>;
    }

    // Get unique site groups to avoid duplicates
    const uniqueGroups = groups.reduce((acc, group) => {
      const key = `${group.site_group_id}`;
      if (!acc[key]) {
        acc[key] = {
          site_group_id: group.site_group_id,
          site_group_name: group.site_group_name,
          customer_name: group.customer_name,
        };
      }
      return acc;
    }, {});

    return (
      <SiteGroupPills>
        {Object.values(uniqueGroups).map((group) => (
          <SiteGroupPill
            key={group.site_group_id}
            title={`${group.site_group_name} (${group.customer_name})`}
          >
            {group.site_group_name}
          </SiteGroupPill>
        ))}
      </SiteGroupPills>
    );
  };

  // Helper function to check if user matches filters
  const userMatchesFilters = (user) => {
    // Search filter
    const matchesSearch =
      !filters.searchQuery ||
      user.email_address
        ?.toLowerCase()
        .includes(filters.searchQuery.toLowerCase()) ||
      user.first_name
        ?.toLowerCase()
        .includes(filters.searchQuery.toLowerCase()) ||
      user.last_name
        ?.toLowerCase()
        .includes(filters.searchQuery.toLowerCase()) ||
      `${user.first_name} ${user.last_name}`
        ?.toLowerCase()
        .includes(filters.searchQuery.toLowerCase());

    // Status filter
    let matchesStatus = true;
    if (filters.hideDeactivated === "active") {
      matchesStatus = user.status === "active";
    } else if (filters.hideDeactivated === "deactivated") {
      matchesStatus = user.status === "deactivated";
    } else if (filters.hideDeactivated === "invited") {
      matchesStatus = user.status === "invited";
    }

    // Role filter
    const matchesRole =
      !filters.role ||
      (user.roles &&
        user.roles.roles &&
        user.roles.roles.includes(filters.role));

    return matchesSearch && matchesStatus && matchesRole;
  };

  const filterCustomer = () => {
    let filtered = customerUsers;

    // Apply adminCustomerToggle if set (legacy filtering from context)
    if (adminCustomerToggle) {
      filtered = filtered.filter(
        (customer) => customer.id === adminCustomerToggle,
      );
    }

    // Apply customer filter from CustomerFilter component
    if (filters.customer) {
      filtered = filtered.filter(
        (customer) => customer.id === filters.customer,
      );
    }

    // Apply user-level filters (search, status, role)
    if (
      filters.searchQuery ||
      filters.hideDeactivated !== "all" ||
      filters.role
    ) {
      filtered = filtered.map((customer) => {
        const filteredClients = customer.clients.filter((clientEmail) => {
          // Find the full user object
          const user = users.find((u) => u.email_address === clientEmail);
          if (!user) return false;

          // Search filter
          const matchesSearch =
            !filters.searchQuery ||
            user.email_address
              ?.toLowerCase()
              .includes(filters.searchQuery.toLowerCase()) ||
            user.first_name
              ?.toLowerCase()
              .includes(filters.searchQuery.toLowerCase()) ||
            user.last_name
              ?.toLowerCase()
              .includes(filters.searchQuery.toLowerCase()) ||
            `${user.first_name} ${user.last_name}`
              ?.toLowerCase()
              .includes(filters.searchQuery.toLowerCase());

          // Status filter
          let matchesStatus = true;
          if (filters.hideDeactivated === "active") {
            matchesStatus = user.status === "active";
          } else if (filters.hideDeactivated === "deactivated") {
            matchesStatus = user.status === "deactivated";
          } else if (filters.hideDeactivated === "invited") {
            matchesStatus = user.status === "invited";
          }
          // If hideDeactivated is "all", matchesStatus stays true (shows all)

          // Role filter
          const matchesRole =
            !filters.role ||
            (user.roles &&
              user.roles.roles &&
              user.roles.roles.includes(filters.role));

          return matchesSearch && matchesStatus && matchesRole;
        });

        // Return customer with filtered clients
        return {
          ...customer,
          clients: filteredClients,
        };
      });

      // Remove customers with no matching users if filters are active
      if (
        filters.searchQuery ||
        filters.role ||
        filters.hideDeactivated !== "all"
      ) {
        filtered = filtered.filter(
          (customer) => customer.clients && customer.clients.length > 0,
        );
      }
    }

    setDisplayCustomerUsers(filtered);

    // Filter unaffiliated users (only show if no specific customer is selected)
    if (filters.customer) {
      // If a customer is selected, don't show unaffiliated users
      setDisplayUnaffiliatedUsers([]);
    } else {
      // Apply filters to unaffiliated users
      const filteredUnaffiliated = unaffiliatedUsers.filter(userMatchesFilters);
      setDisplayUnaffiliatedUsers(filteredUnaffiliated);
    }
  };

  useEffect(() => {
    getUserList();
  }, []);

  // Refresh user list when navigating back to this page
  useEffect(() => {
    // Check if we're on the users list page (not a sub-route like /admin/user/:email)
    const isUsersListPage =
      location.pathname === "/admin" || location.pathname === "/admin/users";

    if (isUsersListPage) {
      //console.log("Navigated back to users list - refreshing data");
      getUserList();
    }
  }, [location.pathname]);

  // Refresh user list when component regains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        //console.log("Tab/window became visible - refreshing user list");
        getUserList();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {}, [loadingMachines]);

  useEffect(() => {
    filterCustomer();
  }, [customerUsers, adminCustomerToggle, filters, users, unaffiliatedUsers]);

  // Calculate global card index counter for proper animation sequencing
  let globalCardIndex = 0;

  return (
    <UsersListSC>
      {loadingUsers || loadingMachines ? (
        <Loader
          size="md"
          content="Loading Users..."
          subtext="Please wait while we fetch user information"
          centered={true}
        />
      ) : window.screen.width >= 768 ? (
        <>
          {/* Customer Filter */}
          <FilterSection>
            <CustomerFilter
              customers={customerUsers}
              users={users}
              unaffiliatedUsers={unaffiliatedUsers}
              onFilterChange={setFilters}
              initialFilters={filters}
            />
          </FilterSection>

          {displayCustomerUsers?.map((customer, index) => (
            <ListTable key={customer.id}>
              <SectionWrapper>
                <SectionRibbon
                  title={customer.name}
                  count={customer.clients?.length}
                  countLabel="Users"
                  icon={PeoplesIcon}
                  variant="primary"
                  rightContent={
                    <Button
                      size="small"
                      variant="outline"
                      onClick={() => copyAllCustomerEmails(customer)}
                    >
                      Copy All Emails
                    </Button>
                  }
                />
                <UserCardsContainer>
                  {customer.clients.map((client) =>
                    users
                      ?.filter(
                        (user) =>
                          user.email_address.includes(client) &&
                          user.status !== "deactivated",
                      )
                      .map((user, index) => (
                        <AnimatedUserCard
                          key={index + "_user"}
                          visible={cardsVisible}
                          delay={globalCardIndex++ * 50}
                          onClick={() =>
                            history.push(`/admin/user/${user.email_address}`)
                          }
                        >
                          <UserCardHeader>
                            <UserAvatar>
                              <Avatar>
                                {(user.first_name
                                  ? user.first_name.charAt(0)
                                  : "") +
                                  " " +
                                  (user.last_name
                                    ? user.last_name.charAt(0)
                                    : "")}
                              </Avatar>
                            </UserAvatar>
                            <UserInfo>
                              <UserName>
                                {user.first_name + " " + user.last_name}
                              </UserName>
                              <UserEmailRow>
                                <UserEmail>{user.email_address}</UserEmail>
                                <CopyButton
                                  size="xs"
                                  appearance="subtle"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(
                                      user.email_address,
                                      "Email copied!",
                                    );
                                  }}
                                  icon={<CopyIcon />}
                                />
                              </UserEmailRow>
                              {user.phone_number && (
                                <UserPhone>
                                  {formatPhoneNumber(user.phone_number)}
                                </UserPhone>
                              )}
                            </UserInfo>
                          </UserCardHeader>

                          <SiteGroupsSection>
                            <SectionLabel>Site Groups</SectionLabel>
                            {getUserSiteGroupsDisplay(user.email_address)}
                          </SiteGroupsSection>

                          <UserCardFooter>
                            <LastActivity>
                              Last active:{" "}
                              {user.last_logged_in !== null
                                ? timeAgo(user.last_logged_in)
                                : "never"}
                            </LastActivity>
                            <UserActions>
                              <StatusBadge status={user.status}>
                                {user.status}
                              </StatusBadge>
                              <RoleBadge>
                                {user.roles["roles"].includes("developer")
                                  ? "DEV"
                                  : user.roles["roles"].includes("superuser")
                                    ? "SUPER"
                                    : user.roles["roles"].includes("admin")
                                      ? "ADMIN"
                                      : user.roles["roles"].includes(
                                            "site-admin",
                                          )
                                        ? "SITE"
                                        : "USER"}
                              </RoleBadge>
                            </UserActions>
                          </UserCardFooter>
                        </AnimatedUserCard>
                      )),
                  )}
                  {/* Include deactivated users in the same container */}
                  {customer.clients.map((client) =>
                    users
                      ?.filter(
                        (user) =>
                          user.email_address.includes(client) &&
                          user.status === "deactivated",
                      )
                      .map((user, index) => (
                        <AnimatedDeactivatedUserCard
                          key={index + "_deactivated"}
                          visible={cardsVisible}
                          delay={globalCardIndex++ * 50}
                          onClick={() =>
                            history.push(`/admin/user/${user.email_address}`)
                          }
                        >
                          <UserCardHeader>
                            <UserAvatar>
                              <Avatar>
                                {(user.first_name
                                  ? user.first_name.charAt(0)
                                  : "") +
                                  " " +
                                  (user.last_name
                                    ? user.last_name.charAt(0)
                                    : "")}
                              </Avatar>
                            </UserAvatar>
                            <UserInfo>
                              <UserName>
                                {user.first_name + " " + user.last_name}
                              </UserName>
                              <UserEmailRow>
                                <UserEmail>{user.email_address}</UserEmail>
                                <CopyButton
                                  size="xs"
                                  appearance="subtle"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(
                                      user.email_address,
                                      "Email copied!",
                                    );
                                  }}
                                  icon={<CopyIcon />}
                                />
                              </UserEmailRow>
                              {user.phone_number && (
                                <UserPhone>
                                  {formatPhoneNumber(user.phone_number)}
                                </UserPhone>
                              )}
                            </UserInfo>
                          </UserCardHeader>

                          <SiteGroupsSection>
                            <SectionLabel>Site Groups</SectionLabel>
                            {getUserSiteGroupsDisplay(user.email_address)}
                          </SiteGroupsSection>

                          <UserCardFooter>
                            <LastActivity>
                              Last active:{" "}
                              {user.last_logged_in !== null
                                ? timeAgo(user.last_logged_in)
                                : "never"}
                            </LastActivity>
                            <UserActions>
                              <StatusBadge status={user.status}>
                                {user.status}
                              </StatusBadge>
                              <RoleBadge>
                                {user.roles["roles"].includes("developer")
                                  ? "DEV"
                                  : user.roles["roles"].includes("superuser")
                                    ? "SUPER"
                                    : user.roles["roles"].includes("admin")
                                      ? "ADMIN"
                                      : user.roles["roles"].includes(
                                            "site-admin",
                                          )
                                        ? "SITE"
                                        : "USER"}
                              </RoleBadge>
                            </UserActions>
                          </UserCardFooter>
                        </AnimatedDeactivatedUserCard>
                      )),
                  )}
                </UserCardsContainer>
              </SectionWrapper>
            </ListTable>
          ))}

          {displayUnaffiliatedUsers?.length > 0 && (
            <ListTable>
              <SectionWrapper>
                <SectionRibbon
                  title="Unaffiliated Users"
                  count={displayUnaffiliatedUsers.length}
                  countLabel="Users"
                  icon={PeoplesIcon}
                  variant="unaffiliated"
                />

                <UserCardsContainer>
                  {/* Active unaffiliated users */}
                  {displayUnaffiliatedUsers
                    ?.filter((user) => user.status !== "deactivated")
                    .map((user, index) => (
                      <AnimatedUserCard
                        key={index + "_unaffuser"}
                        visible={cardsVisible}
                        delay={globalCardIndex++ * 50}
                        onClick={() =>
                          history.push(`/admin/user/${user.email_address}`)
                        }
                      >
                        <UserCardHeader>
                          <UserAvatar>
                            <Avatar>
                              {(user.first_name
                                ? user.first_name.charAt(0)
                                : "") +
                                " " +
                                (user.last_name
                                  ? user.last_name.charAt(0)
                                  : "")}
                            </Avatar>
                          </UserAvatar>
                          <UserInfo>
                            <UserName>
                              {user.first_name + " " + user.last_name}
                            </UserName>
                            <UserEmailRow>
                              <UserEmail>{user.email_address}</UserEmail>
                              <CopyButton
                                size="xs"
                                appearance="subtle"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(
                                    user.email_address,
                                    "Email copied!",
                                  );
                                }}
                                icon={<CopyIcon />}
                              />
                            </UserEmailRow>
                            {user.phone_number && (
                              <UserPhone>
                                {formatPhoneNumber(user.phone_number)}
                              </UserPhone>
                            )}
                          </UserInfo>
                        </UserCardHeader>

                        <SiteGroupsSection>
                          <SectionLabel>Site Groups</SectionLabel>
                          {getUserSiteGroupsDisplay(user.email_address)}
                        </SiteGroupsSection>

                        <UserCardFooter>
                          <LastActivity>
                            Last active:{" "}
                            {user.last_logged_in !== null
                              ? timeAgo(user.last_logged_in)
                              : "never"}
                          </LastActivity>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <StatusBadge status={user.status}>
                              {user.status}
                            </StatusBadge>
                            <RoleBadge>
                              {user.roles["roles"].includes("developer")
                                ? "DEV"
                                : user.roles["roles"].includes("superuser")
                                  ? "SUPER"
                                  : user.roles["roles"].includes("admin")
                                    ? "ADMIN"
                                    : user.roles["roles"].includes("site-admin")
                                      ? "SITE"
                                      : "USER"}
                            </RoleBadge>
                          </div>
                        </UserCardFooter>
                      </AnimatedUserCard>
                    ))}

                  {/* Deactivated unaffiliated users */}
                  {displayUnaffiliatedUsers
                    ?.filter((user) => user.status === "deactivated")
                    .map((user, index) => (
                      <AnimatedDeactivatedUserCard
                        key={index + "_unaffuser_deactivated"}
                        visible={cardsVisible}
                        delay={globalCardIndex++ * 50}
                        onClick={() =>
                          history.push(`/admin/user/${user.email_address}`)
                        }
                      >
                        <UserCardHeader>
                          <UserAvatar>
                            <Avatar>
                              {(user.first_name
                                ? user.first_name.charAt(0)
                                : "") +
                                " " +
                                (user.last_name
                                  ? user.last_name.charAt(0)
                                  : "")}
                            </Avatar>
                          </UserAvatar>
                          <UserInfo>
                            <UserName>
                              {user.first_name + " " + user.last_name}
                            </UserName>
                            <UserEmailRow>
                              <UserEmail>{user.email_address}</UserEmail>
                              <CopyButton
                                size="xs"
                                appearance="subtle"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(
                                    user.email_address,
                                    "Email copied!",
                                  );
                                }}
                                icon={<CopyIcon />}
                              />
                            </UserEmailRow>
                            {user.phone_number && (
                              <UserPhone>
                                {formatPhoneNumber(user.phone_number)}
                              </UserPhone>
                            )}
                          </UserInfo>
                        </UserCardHeader>

                        <SiteGroupsSection>
                          <SectionLabel>Site Groups</SectionLabel>
                          {getUserSiteGroupsDisplay(user.email_address)}
                        </SiteGroupsSection>

                        <UserCardFooter>
                          <LastActivity>
                            Last active:{" "}
                            {user.last_logged_in !== null
                              ? timeAgo(user.last_logged_in)
                              : "never"}
                          </LastActivity>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <StatusBadge status={user.status}>
                              {user.status}
                            </StatusBadge>
                            <RoleBadge>
                              {user.roles["roles"].includes("developer")
                                ? "DEV"
                                : user.roles["roles"].includes("superuser")
                                  ? "SUPER"
                                  : user.roles["roles"].includes("admin")
                                    ? "ADMIN"
                                    : user.roles["roles"].includes("site-admin")
                                      ? "SITE"
                                      : "USER"}
                            </RoleBadge>
                          </div>
                        </UserCardFooter>
                      </AnimatedDeactivatedUserCard>
                    ))}
                </UserCardsContainer>
              </SectionWrapper>
            </ListTable>
          )}
        </>
      ) : (
        <MobileUserCardsContainer>
          {users?.map((user, index) => (
            <UserCard
              key={index}
              onClick={() => history.push(`/admin/user/${user.email_address}`)}
            >
              <UserCardHeader>
                <UserAvatar>
                  <Avatar>
                    {(user.first_name ? user.first_name.charAt(0) : "") +
                      " " +
                      (user.last_name ? user.last_name.charAt(0) : "")}
                  </Avatar>
                  <RoleIndicator>
                    {user.roles["roles"].includes("developer") ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="#673ab7"
                        style={{ verticalAlign: "middle" }}
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zM4.756 4.566c.763-1.424 4.02-.12.952 3.434-4.496-1.596-2.35-4.298-.952-3.434zm6.559 5.448a.5.5 0 0 1 .548.736A4.498 4.498 0 0 1 7.965 13a4.498 4.498 0 0 1-3.898-2.25.5.5 0 0 1 .548-.736h.005l.017.005.067.015.252.055c.215.046.515.108.857.169.693.124 1.522.242 2.152.242.63 0 1.46-.118 2.152-.242a26.58 26.58 0 0 0 1.109-.224l.067-.015.017-.004.005-.002zm-.07-5.448c1.397-.864 3.543 1.838-.953 3.434-3.067-3.554.19-4.858.952-3.434z" />
                      </svg>
                    ) : user.roles["roles"].includes("superuser") ? (
                      <AdminIcon color="red" style={{ fontSize: "1em" }} />
                    ) : user.roles["roles"].includes("admin") ? (
                      <AdminIcon color="#f5a623" style={{ fontSize: "1em" }} />
                    ) : user.roles["roles"].includes("site-admin") ? (
                      <AdminIcon
                        color="#23baf5ff"
                        style={{ fontSize: "1em" }}
                      />
                    ) : null}
                  </RoleIndicator>
                </UserAvatar>
                <UserInfo>
                  <UserName>{user.first_name + " " + user.last_name}</UserName>
                  <UserEmailRow>
                    <UserEmail>{user.email_address}</UserEmail>
                    <CopyButton
                      size="xs"
                      appearance="subtle"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(user.email_address, "Email copied!");
                      }}
                      icon={<CopyIcon />}
                    />
                  </UserEmailRow>
                  {user.phone_number && (
                    <UserPhone>
                      {formatPhoneNumber(user.phone_number)}
                    </UserPhone>
                  )}
                </UserInfo>
              </UserCardHeader>

              <SiteGroupsSection>
                <SectionLabel>Site Groups</SectionLabel>
                {getUserSiteGroupsDisplay(user.email_address)}
              </SiteGroupsSection>

              <UserCardFooter>
                <LastActivity>
                  Last active:{" "}
                  {user.last_logged_in !== null
                    ? timeAgo(user.last_logged_in)
                    : "never"}
                </LastActivity>
                <div style={{ display: "flex", gap: "8px" }}>
                  <StatusBadge status={user.status}>{user.status}</StatusBadge>
                  <RoleBadge>
                    {user.roles["roles"].includes("developer")
                      ? "DEV"
                      : user.roles["roles"].includes("superuser")
                        ? "SUPER"
                        : user.roles["roles"].includes("admin")
                          ? "ADMIN"
                          : user.roles["roles"].includes("site-admin")
                            ? "SITE"
                            : "USER"}
                  </RoleBadge>
                </div>
              </UserCardFooter>
            </UserCard>
          ))}
        </MobileUserCardsContainer>
      )}
    </UsersListSC>
  );
};

const UsersListSC = styled.div`
  display: block;
  width: 100%;
  height: auto;
  text-align: -webkit-center;
  color: ${({ theme, darkMode }) =>
    darkMode ? theme.dark_accent : theme.light_accent};

  @media screen and (min-width: 769px) {
  }
`;

const FilterSection = styled.div`
  width: 70%;
  margin: 0 auto 20px;
  padding-top: 20px;

  @media screen and (max-width: 768px) {
    width: 95%;
    padding-top: 16px;
  }
`;

const ListTable = styled.div`
  width: 100%;
  height: auto;
  padding-top: 8px;
  color: ${({ theme, darkMode }) =>
    darkMode ? theme.dark_accent : theme.light_accent};
  text-align: start;

  @media screen and (min-width: 769px) {
    width: 70%;
  }

  .rs-flex-box-grid {
    align-items: center;
  }
`;

const SectionWrapper = styled.div`
  position: relative;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.08) 0%,
    rgba(255, 255, 255, 0.04) 100%
  );
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 32px;
  padding: 0 0 16px 0;
  margin-bottom: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  overflow: visible;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: rgba(255, 255, 255, 0.18);
  }

  /* Make the SectionRibbon sit on top edge */
  > div:first-child {
    margin: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  @media screen and (max-width: 768px) {
    padding: 0 0 12px 0;
    margin-bottom: 12px;
  }
`;

const SiteGroupPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: flex-start;
  max-width: 100%;
`;

const SiteGroupPill = styled.span`
  background: linear-gradient(
    135deg,
    rgba(74, 144, 226, 0.15) 0%,
    rgba(155, 89, 182, 0.15) 100%
  );
  color: #4a90e2;
  border: 1px solid rgba(74, 144, 226, 0.2);
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.8em;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.6),
      transparent
    );
  }
  }
`;

const NoSiteGroups = styled.span`
  background: linear-gradient(
    135deg,
    rgba(149, 165, 166, 0.15) 0%,
    rgba(127, 140, 141, 0.15) 100%
  );
  color: #95a5a6;
  border: 1px solid rgba(149, 165, 166, 0.2);
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.8em;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  display: inline-block;
  width: fit-content;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.6),
      transparent
    );
  }
`;

// 🌟 Glassy Card Design Components
const UserCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 15px 20px 10px 20px;
  width: 100%;

  @media screen and (max-width: 768px) {
    gap: 6px;
    padding: 10px 16px 8px 16px;
  }
`;

const UserCard = styled.div`
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.25) 0%,
    rgba(255, 255, 255, 0.1) 100%
  );
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  padding: 16px 20px;
  cursor: pointer;
  transition:
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.07),
    0 1px 3px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;
  will-change: transform;
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 80px;

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 6px 16px rgba(0, 0, 0, 0.08),
      0 3px 8px rgba(0, 0, 0, 0.06);
    border-color: rgba(255, 255, 255, 0.3);
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0.15) 100%
    );
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    will-change: opacity;
  }
`;

const AnimatedUserCard = styled(UserCard)`
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transform: ${(props) =>
    props.visible ? "translateY(0)" : "translateY(15px)"};
  transition:
    opacity 0.4s ease-out ${(props) => props.delay}ms,
    transform 0.4s ease-out ${(props) => props.delay}ms,
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity, transform;

  &:hover {
    transform: ${(props) =>
      props.visible ? "translateY(-2px)" : "translateY(15px)"};
  }
`;

const UserCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const UserAvatar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RoleIndicator = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 1.1em;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const UserEmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UserEmail = styled.div`
  font-size: 0.9em;
  color: #7f8c8d;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const UserPhone = styled.div`
  font-size: 0.85em;
  color: #95a5a6;
  margin-top: 2px;
`;

const CopyButton = styled(IconButton)`
  &&& {
    padding: 2px 4px;
    min-width: auto;
    height: auto;
    opacity: 0.6;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
      background: rgba(52, 152, 219, 0.1);
    }

    .rs-icon {
      font-size: 14px;
    }
  }
`;

const StatusBadge = styled.div`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: 500;
  backdrop-filter: blur(10px);
  border: 1px solid;

  ${(props) => {
    if (props.status === "active") {
      return `
            background: rgba(46, 204, 113, 0.2);
            color: #27ae60;
            border-color: rgba(46, 204, 113, 0.3);
         `;
    } else if (props.status === "invited") {
      return `
            background: rgba(149, 165, 166, 0.2);
            color: #7f8c8d;
            border-color: rgba(149, 165, 166, 0.3);
         `;
    } else {
      return `
            background: rgba(231, 76, 60, 0.2);
            color: #e74c3c;
            border-color: rgba(231, 76, 60, 0.3);
         `;
    }
  }}
`;

const RoleBadge = styled.div`
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 0.75em;
  font-weight: 600;
  background: linear-gradient(
    135deg,
    rgba(52, 152, 219, 0.2),
    rgba(155, 89, 182, 0.2)
  );
  color: #3498db;
  border: 1px solid rgba(52, 152, 219, 0.3);
  backdrop-filter: blur(10px);
`;

const SiteGroupsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 200px;
  max-width: 300px;
`;

const SectionLabel = styled.div`
  font-size: 0.75em;
  font-weight: 600;
  color: #5d6d7e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const UserCardFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  min-width: 180px;
`;

const UserActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const LastActivity = styled.div`
  font-size: 0.8em;
  color: #95a5a6;
  font-style: italic;
`;

const MobileUserCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
  width: 100%;
`;

const AnimatedDeactivatedUserCard = styled(AnimatedUserCard)`
  background: linear-gradient(
    135deg,
    rgba(231, 76, 60, 0.15) 0%,
    rgba(192, 57, 43, 0.1) 100%
  );
  border-color: rgba(231, 76, 60, 0.2);
  opacity: ${(props) => (props.visible ? 0.8 : 0)};

  &:hover {
    opacity: ${(props) => (props.visible ? 1 : 0)};
    background: linear-gradient(
      135deg,
      rgba(231, 76, 60, 0.2) 0%,
      rgba(192, 57, 43, 0.15) 100%
    );
    border-color: rgba(231, 76, 60, 0.3);
  }
`;
